<template>
  <section class="distributed-hybrid-master column items-center q-pt-md">
    <workload-form-wrapper
      :form-state="hybridWorkloadModel"
      :submitting="submitting"
      submit-btn-text="Create Training"
      cancel-btn-text="Back"
      :blurred="isDrawerOpen"
      init-dirty
      @unblur="closeDrawer"
      @canceled="onBack"
      @submit="onSubmit"
      @leave-page="onLeave"
    >
      <toggle-editing-form
        aid="toggle-editing-form"
        :is-enabled="!formMetaData.sameAsWorker"
        :section-options="masterToggleSectionOptions"
        @toggle-editing-changed="updateSameAsWorker"
        :section-disabled="formMetaData.sameAsWorkerDisabled"
      />

      <hybrid-environment-section
        :key="`he-${selectedTemplates.environment.sectionKey}`"
        :render-key="selectedTemplates.environment.renderKey.toString()"
        :selected-name="selectedEnvironmentName"
        :workload-spec="hybridWorkloadModel.spec"
        :environment-variable-source-value-options="environmentVariablesSourceValueOptions"
        :policy="policy"
        :cluster-id="formMetaData.clusterId"
        :support-config-map-as-env-var="supportConfigMapAsEnvVar"
        :section-disabled="formMetaData.sameAsWorker"
        :disable-fields="{ toolsAccess: formMetaData.sameAsWorker }"
        @load-section-templates="displayEnvironmentsList"
        @update="updateModelSpec"
        @reset="resetEnvironment"
        @clear="clearEnvironment"
      />

      <hybrid-compute-resource-section
        :section-disabled="formMetaData.sameAsWorker"
        :key="`hc-${selectedTemplates['compute-resource'].sectionKey}`"
        :selected-name="selectedComputeName"
        :workload-spec="hybridWorkloadModel.spec"
        :project-node-types="projectData.selectedProjectNodeTypes"
        :node-pools-resources="projectData.projectNodePoolsResources"
        :policy="policy"
        :support-multi-fraction-gpu="supportMultiFractionGpu"
        @load-section-templates="displayComputesList"
        @update="updateModelSpec"
        @reset="resetCompute"
        @clear="clearCompute"
      />

      <hybrid-data-source-section
        :section-disabled="formMetaData.sameAsWorker"
        :default-opened="shouldOpenDataSourceSection"
        :workload-storage="masterSpec.storage"
        @load-section-templates="displayDataSourcesList"
        @update="updateModelStorage"
        :secrets="secrets"
        :policy-rules="policy?.rules?.storage"
        :policy-defaults="policy?.defaults?.storage"
        :allowed-data-source-types="allowedDataSources"
        :cluster-id="formMetaData.clusterId"
      />

      <general-section
        :section-disabled="formMetaData.sameAsWorker"
        should-auto-validate
        :workload-form-type="EWorkloadFormType.Distributed"
        :distributed-replica-type="EDistributedReplicaType.Master"
        :general-model="generalSectionModel"
        :show-backoff-limit="supportBackoffLimit"
        :policy-rules="policy?.rules"
        :itemize-defaults="generalSectionItemizeDefaults"
        :show-termination-grace-period="isClusterSupportTerminationGracePeriod"
        @general-model-changed="updateGeneralModel"
      />
    </workload-form-wrapper>

    <workload-creation-drawer
      :is-open="isDrawerOpen"
      :comply-to="drawerFilters"
      :entity="drawerDataEntity"
      :policy-details="policyViewDetails"
      :created-entity-id="recentlyCreatedEntityId"
      :allowed-data-source-options="allowedDataSources"
      @close="closeDrawer"
      @select="selectTemplate"
      @select-multiple="selectMultipleTemplates"
      @create-new="onCreateNew"
    />
  </section>
</template>

<script lang="ts">
import type { PropType } from "vue";
import { defineComponent } from "vue";
// Components
import { WorkloadCreationDrawer } from "@/components/workload-creation/workload-creation-drawer";
import { HybridEnvironmentSection } from "@/components/workload-creation/hybrid-environment-section";
import { HybridComputeResourceSection } from "@/components/workload-creation/hybrid-compute-resource-section";
import { WorkloadFormWrapper } from "@/components/workload/workload-form-wrapper";
import { HybridDataSourceSection } from "@/components/workload-creation/hybrid-data-source-section";
import { GeneralSection } from "@/components/section/general-section";
// Stores & Services
import { useTrainingStore } from "@/stores/training.store";
import { useClusterStore } from "@/stores/cluster.store";
import { deepCopy } from "@/utils/common.util";
// Models
import type { DistributedFramework } from "@/swagger-models/assets-service-client";
import { AssetKind } from "@/swagger-models/assets-service-client";
import { WorkloadCreationDrawerEntities } from "@/models/workload-creation-drawer-model";
import { EDistributedReplicaType, EWorkloadType } from "@/models/workload.model";
import { type IPolicyViewDetails } from "@/utils/policy.util/policy-view.util";
import { EWorkloadFormType } from "@/models/workload.model";
import {
  MIN_CLUSTER_VERSION_FOR_CONFIG_MAP_ENV_VAR,
  MIN_CLUSTER_VERSION_FOR_MULTI_FRACTION_GPU,
  MIN_CLUSTER_VERSION_FOR_TERMINATION_GRACE_PERIOD,
} from "@/common/version.constant";
import type { TDataSourceKinds } from "@/models/data-source.model";
import type { IAssetsFilter } from "@/models/filter.model";
import type { IUIGeneralSectionModel } from "@/components/section/general-section";
import type { IItemizedListItem } from "@/components/common/runai-itemized-list";
import type { IUISuperWorkloadSpec, IUIStorageFields } from "@/models/workload.model";
import type { ILoadedItemizeDefaults } from "@/models/policy.model";
import { type UnifiedPolicyInfoPerReplicaType } from "@/swagger-models/workloads-client";
import type { SecretListInfo } from "@/swagger-models/k8s-objects-tracker-client";
import type { TEnvVarNoneCustomSourceOption } from "@/models/environment.model";
import type { IWorkloadToggleEditingFormSectionOptions } from "@/components/section/toggle-editing-form";
import { ToggleEditingForm } from "@/components/section/toggle-editing-form";
import { errorMessages } from "@/common/error-message.constant";
import type { TProjectData } from "./distributed-hybrid-worker.vue";
import { EHybridFormTypes } from "@/composables/helpers/use-hybrid-templates.mapper";
import { useHybridTemplates } from "@/composables/use-hybrid-templates.composable";

export type TDistributedMasterFormMetaData = {
  clusterId: string;
  projectId: string;
  sameAsWorker: boolean;
  sameAsWorkerDisabled: boolean;
  framework?: DistributedFramework;
};

export default defineComponent({
  name: "distributed-hybrid-master",
  components: {
    WorkloadCreationDrawer,
    HybridEnvironmentSection,
    HybridComputeResourceSection,
    WorkloadFormWrapper,
    HybridDataSourceSection,
    GeneralSection,
    ToggleEditingForm,
  },
  emits: ["submit", "update", "save-form-state", "back", "update-same-as-worker", "leave-page"],
  props: {
    masterSpec: {
      type: Object as PropType<IUISuperWorkloadSpec>,
      required: true,
    },
    formMetaData: {
      type: Object as PropType<TDistributedMasterFormMetaData>,
      required: true,
    },
    projectData: {
      type: Object as PropType<TProjectData>,
      required: true,
    },
    submitting: {
      type: Boolean as PropType<boolean>,
      required: true,
    },
    policy: {
      type: [Object, null] as PropType<UnifiedPolicyInfoPerReplicaType | null>,
      required: false,
    },
    policyViewDetails: {
      type: Object as PropType<IPolicyViewDetails>,
      required: false,
    },
    environmentVariablesSourceValueOptions: {
      type: Array as PropType<TEnvVarNoneCustomSourceOption[]>,
      required: false,
    },
    secrets: {
      type: Array as PropType<SecretListInfo[]>,
      required: false,
      default: () => [],
    },
    allowedDataSources: {
      type: Object as PropType<Set<TDataSourceKinds>>,
      required: false,
    },
  },
  data() {
    return {
      trainingStore: useTrainingStore(),
      clusterStore: useClusterStore(),

      EWorkloadFormType,
      EDistributedReplicaType,
    };
  },
  setup(props) {
    const {
      // state
      isDrawerOpen,
      drawerDataEntity,
      hybridWorkloadModel,
      selectedTemplates,
      recentlyCreatedEntityId,
      // methods
      closeDrawer,
      displayEnvironmentsList,
      displayComputesList,
      displayDataSourcesList,
      handleSelection,
      handleMultiSelection,
      createSectionsClearedStates,
      clearComputeTemplate,
      clearEnvironmentTemplate,
      resetEnvironmentToDefault,
      resetComputeToDefault,
      handleCreatedAssets,
      reRenderSectionEntirely,
      onCreateNewSetup,
      // computed
      selectedEnvironmentName,
      selectedComputeName,
    } = useHybridTemplates(EHybridFormTypes.DistributedMaster, {
      name: "",
      projectId: props.formMetaData.projectId,
      clusterId: props.formMetaData.clusterId,
      spec: props.masterSpec,
    });

    return {
      // state
      isDrawerOpen,
      drawerDataEntity,
      hybridWorkloadModel,
      selectedTemplates,
      recentlyCreatedEntityId,
      // methods
      closeDrawer,
      displayEnvironmentsList,
      displayComputesList,
      displayDataSourcesList,
      handleSelection,
      handleMultiSelection,
      createSectionsClearedStates,
      clearComputeTemplate,
      clearEnvironmentTemplate,
      resetEnvironmentToDefault,
      resetComputeToDefault,
      handleCreatedAssets,
      reRenderSectionEntirely,
      onCreateNewSetup,
      // computed
      selectedEnvironmentName,
      selectedComputeName,
    };
  },
  async created() {
    const { kind, createdEntityId, workloadFormName } = this.$route.query;

    if (workloadFormName === EDistributedReplicaType.Master) {
      const query = deepCopy(this.$route.query);
      delete query.workloadFormName;
      delete query.kind;
      delete query.createdEntityId;
      await this.$router.replace({
        query: { ...query },
      });
      this.handleCreatedAssets(kind?.toString(), createdEntityId?.toString());
    }

    this.createSectionsClearedStates();

    const formState = this.trainingStore.getDistributedMasterFlatFormState();
    if (formState) this.loadFormState();
  },
  computed: {
    masterToggleSectionOptions(): IWorkloadToggleEditingFormSectionOptions {
      return {
        mainTitle: "Allow different setup for the master",
        disabledTooltip: this.formMetaData.sameAsWorkerDisabled ? errorMessages.CANT_BE_MODIFIED_POLICY : "",
        tooltip: `
          When enabled, you can change the master's setup to be different from the workers'.
          When disabled, the master will inherit the workers' setup automatically.
          `,
      };
    },
    shouldOpenDataSourceSection(): boolean {
      return (
        !!this.$route.query.createdEntityId &&
        [
          AssetKind.HostPath,
          AssetKind.Nfs,
          AssetKind.Pvc,
          AssetKind.Git,
          AssetKind.S3,
          AssetKind.ConfigMap,
          AssetKind.SecretVolume,
          AssetKind.DataVolume,
        ].includes(this.$route.query.kind as TDataSourceKinds)
      );
    },

    drawerFilters(): IAssetsFilter {
      return {
        projectId: +this.formMetaData.projectId,
        complyToProject: +this.formMetaData.projectId,
        complyToWorkloadType: EWorkloadType.Distributed,
        complyToReplicaType: "Master",
        distributedFramework: this.formMetaData.framework,
        isDistributed: true,
      };
    },
    generalSectionModel(): IUIGeneralSectionModel {
      return {
        autoDeletionTimeAfterCompletionSeconds: Number.isInteger(
          this.hybridWorkloadModel.spec?.autoDeletionTimeAfterCompletionSeconds,
        )
          ? this.hybridWorkloadModel.spec?.autoDeletionTimeAfterCompletionSeconds
          : null,
        annotations: (this.hybridWorkloadModel.spec?.annotations as IItemizedListItem[]) || undefined,
        labels: (this.hybridWorkloadModel.spec?.labels as IItemizedListItem[]) || undefined,
        backoffLimit: this.hybridWorkloadModel.spec?.backoffLimit || null,
        terminationGracePeriodSeconds: this.hybridWorkloadModel.spec?.terminationGracePeriodSeconds || null,
      };
    },
    generalSectionItemizeDefaults(): ILoadedItemizeDefaults {
      return {
        environmentVariables: this.policy?.defaults?.environmentVariables,
        annotations: this.policy?.defaults?.annotations,
        labels: this.policy?.defaults?.labels,
        tolerations: this.policy?.defaults?.tolerations,
      };
    },
    supportBackoffLimit(): boolean {
      return this.clusterStore.isClusterVersionSupportBackoffLimit(this.formMetaData.clusterId);
    },
    isClusterSupportTerminationGracePeriod(): boolean {
      return this.clusterStore.isClusterVersionSufficient(
        this.formMetaData.clusterId,
        MIN_CLUSTER_VERSION_FOR_TERMINATION_GRACE_PERIOD,
      );
    },
    supportMultiFractionGpu(): boolean {
      return this.clusterStore.isClusterVersionSufficient(
        this.formMetaData.clusterId || "",
        MIN_CLUSTER_VERSION_FOR_MULTI_FRACTION_GPU,
      );
    },
    supportConfigMapAsEnvVar(): boolean {
      return this.clusterStore.isClusterVersionSufficient(
        this.formMetaData.clusterId || "",
        MIN_CLUSTER_VERSION_FOR_CONFIG_MAP_ENV_VAR,
      );
    },
    disableFormComponents(): boolean {
      return this.formMetaData.sameAsWorker;
    },
  },
  methods: {
    updateParent(): void {
      this.$emit("update", this.hybridWorkloadModel.spec);
    },
    updateModelSpec(spec: IUISuperWorkloadSpec): void {
      this.hybridWorkloadModel.spec = spec;
      this.updateParent();
    },
    updateSameAsWorker(val: boolean): void {
      const isSameAsWorker = !val;
      this.$emit("update-same-as-worker", isSameAsWorker);
    },
    matchModelToSameAsWorkerChanges(): void {
      if (!this.formMetaData.sameAsWorker) return;

      this.hybridWorkloadModel.spec = this.masterSpec;

      this.selectedTemplates[WorkloadCreationDrawerEntities.Environment].name = "";
      this.selectedTemplates[WorkloadCreationDrawerEntities.Environment].defaultData = null;
      this.selectedTemplates[WorkloadCreationDrawerEntities.Compute].name = "";
      this.selectedTemplates[WorkloadCreationDrawerEntities.Compute].defaultData = null;

      this.reRenderSectionEntirely(WorkloadCreationDrawerEntities.Environment);
      this.reRenderSectionEntirely(WorkloadCreationDrawerEntities.Compute);
      this.createSectionsClearedStates();
    },
    updateModelStorage(storage: IUIStorageFields): void {
      this.hybridWorkloadModel.spec = {
        ...this.hybridWorkloadModel.spec,
        storage,
      };
      this.updateParent();
    },
    saveFormState(): void {
      this.$emit("save-form-state", this.selectedTemplates);
    },
    loadFormState(): void {
      const formState = this.trainingStore.getDistributedMasterFlatFormState();
      if (!formState) return;

      this.selectedTemplates = formState?.selectedTemplates;
    },
    clearFormState(): void {
      this.trainingStore.clearDistributedMasterFlatFormState();
    },
    onLeave(): void {
      this.$emit("leave-page");
    },
    onSubmit(): void {
      this.$emit("submit");
    },
    onBack(): void {
      this.clearFormState();
      this.$emit("back");
    },
    updateGeneralModel(generalModel: IUIGeneralSectionModel): void {
      this.hybridWorkloadModel.spec = {
        ...this.hybridWorkloadModel.spec,
        autoDeletionTimeAfterCompletionSeconds: generalModel.autoDeletionTimeAfterCompletionSeconds,
        annotations: generalModel.annotations,
        labels: generalModel.labels,
        backoffLimit: generalModel.backoffLimit,
        terminationGracePeriodSeconds: generalModel.terminationGracePeriodSeconds,
      };
      this.updateParent();
    },
    async selectTemplate(itemIdAndName: { id: string; name: string }) {
      await this.handleSelection(itemIdAndName);
      this.updateParent();
    },
    async selectMultipleTemplates(selectedItems: { id: string; name: string; kind: AssetKind }[]) {
      await this.handleMultiSelection(selectedItems);
      this.updateParent();
    },
    clearCompute(): void {
      this.clearComputeTemplate();
      this.updateParent();
    },
    clearEnvironment(): void {
      this.clearEnvironmentTemplate();
      this.updateParent();
    },
    resetEnvironment(): void {
      this.resetEnvironmentToDefault();
      this.updateParent();
    },
    resetCompute(): void {
      this.resetComputeToDefault();
      this.updateParent();
    },
    onCreateNew(type: string): void {
      this.saveFormState();
      this.onCreateNewSetup(type, {
        distFramework: this.formMetaData.framework,
        workloadType: EWorkloadFormType.Training,
        workloadFormName: EDistributedReplicaType.Master,
      });
    },
  },
  watch: {
    formMetaData: {
      handler(newVal: TDistributedMasterFormMetaData) {
        if (newVal.sameAsWorker) {
          // if sameAsWorker is true, we need to match the local model to the model from the props
          this.$nextTick(() => {
            this.matchModelToSameAsWorkerChanges();
          });
        }
      },
      deep: true,
    },
  },
});
</script>
